import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import {
  COOKIE_SETTINGS_EVENT,
  CookieConsent,
  applyConsent,
  loadConsent,
  saveConsent,
} from "../../common/cookies";

const navy = "#1A3348";
const blue = "#1E5A8A";
const blueDark = "#154266";

const pillSx = {
  borderRadius: "999px !important",
  textTransform: "none",
  fontWeight: 800,
  minHeight: 44,
  px: 2.25,
};

const ghostSx = {
  ...pillSx,
  borderColor: "rgba(26, 51, 72, 0.28)",
  color: navy,
  backgroundColor: "#FFFFFF",
  "&:hover": {
    borderColor: blue,
    backgroundColor: "#F4F7FA",
    color: navy,
  },
};

const primarySx = {
  ...pillSx,
  bgcolor: blue,
  color: "#FFFFFF",
  boxShadow: "0 8px 22px rgba(30, 90, 138, 0.28)",
  "&:hover": { bgcolor: blueDark, color: "#FFFFFF" },
};

const CookieBanner = () => {
  const [ready, setReady] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = loadConsent();
    if (saved) {
      applyConsent(saved);
      setPrefs(saved);
      setShowBar(false);
      return;
    }
    setShowBar(true);
  }, []);

  useEffect(() => {
    if (!showBar) {
      setReady(false);
      return;
    }
    const show = () => setReady(true);
    const idle = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (idle.requestIdleCallback) {
      const id = idle.requestIdleCallback(show, { timeout: 2800 });
      return () => idle.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(show, 1800);
    return () => window.clearTimeout(timer);
  }, [showBar]);

  useEffect(() => {
    const open = () => {
      const saved = loadConsent();
      if (saved) setPrefs(saved);
      setSettingsOpen(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const text = useMemo(
    () => (
      <>
        Este sitio utiliza cookies para mejorar tu experiencia. Al continuar, aceptas su uso.{" "}
        <Link href="/cookies" underline="always" sx={{ color: blue, fontWeight: 800 }}>
          Política de cookies
        </Link>
        .
      </>
    ),
    []
  );

  const persist = (next: { analytics: boolean; marketing: boolean }) => {
    setPrefs(saveConsent(next));
    setShowBar(false);
    setSettingsOpen(false);
  };

  if (!ready && !settingsOpen) return null;

  return (
    <>
      {ready && showBar && (
        <Box
          role="region"
          aria-label="Preferencias de cookies"
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: { xs: 12, md: 18 },
            zIndex: 1300,
            display: "flex",
            justifyContent: "center",
            px: { xs: 1.25, md: 2 },
          }}
        >
          <Box
            sx={{
              width: "min(1100px, 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1.1, md: 2.25 },
              textAlign: { xs: "center", md: "left" },
              p: { xs: 1.35, md: 2.25 },
              pl: { xs: 1.35, md: 2.75 },
              borderRadius: { xs: "14px", md: "18px" },
              border: "1px solid rgba(26, 51, 72, 0.12)",
              borderLeft: { xs: "1px solid rgba(26, 51, 72, 0.12)", md: `6px solid ${blue}` },
              bgcolor: "rgba(255, 255, 255, 0.94)",
              color: navy,
              backdropFilter: "blur(14px)",
              boxShadow: "0 18px 50px rgba(26, 51, 72, 0.18)",
            }}
          >
            <Typography sx={{ m: 0, lineHeight: 1.45, fontSize: { xs: "0.86rem", md: "0.98rem" }, maxWidth: "72ch", color: navy }}>
              {text}
            </Typography>
            <Stack
              direction="row"
              spacing={1.25}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ xs: "center", md: "flex-end" }}
              sx={{ flex: "0 0 auto", width: { xs: "100%", md: "auto" } }}
            >
              <Button variant="outlined" onClick={() => setSettingsOpen(true)} sx={ghostSx}>
                Configuración de cookies
              </Button>
              <Button variant="contained" onClick={() => persist({ analytics: true, marketing: true })} sx={primarySx}>
                Aceptar todo
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#FFFFFF",
            border: "1px solid rgba(26, 51, 72, 0.10)",
            color: navy,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: navy }}>Configuración de cookies</DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(26, 51, 72, 0.10)" }}>
          <Typography sx={{ color: "#4B5D6B", mb: 2, lineHeight: 1.6 }}>
            Elige qué cookies permitir. Las cookies necesarias siempre están activas.
          </Typography>
          <Stack spacing={1.25}>
            <FormControlLabel
              control={<Checkbox checked disabled sx={{ color: blue, "&.Mui-checked": { color: blue } }} />}
              label="Necesarias"
              sx={{ "& .MuiFormControlLabel-label": { fontWeight: 700, color: navy } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={prefs.marketing}
                  onChange={(event) => setPrefs((prev) => ({ ...prev, marketing: event.target.checked }))}
                  sx={{ color: navy, "&.Mui-checked": { color: blue } }}
                />
              }
              label="Publicidad"
              sx={{ "& .MuiFormControlLabel-label": { fontWeight: 700, color: navy } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => persist({ analytics: false, marketing: false })}
            sx={ghostSx}
          >
            Solo necesarias
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button variant="outlined" onClick={() => setSettingsOpen(false)} sx={ghostSx}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => persist(prefs)} sx={primarySx}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieBanner;
